/* eslint-disable class-methods-use-this */

// Horizontal scroll track with prev/next arrows. Arrows step to the next/previous
// item edge so tracks with mixed item widths still land on each item.
class ScrollCarousel extends HTMLElement {
  get trackSelector() {
    return '[data-carousel-track]';
  }

  get itemSelector() {
    return '[data-carousel-item]';
  }

  connectedCallback() {
    this.track = this.querySelector(this.trackSelector);
    this.prevButton = this.querySelector('[data-carousel-prev]');
    this.nextButton = this.querySelector('[data-carousel-next]');

    if (!this.track) return;

    this.lastScrollAt = 0;

    this.setupArrows();

    this.onScroll = () => {
      this.lastScrollAt = Date.now();
      this.updateArrows();
    };
    this.track.addEventListener('scroll', this.onScroll, { passive: true });

    this.onResize = this.debounce(() => this.updateArrows(), 150);
    window.addEventListener('resize', this.onResize);

    this.updateArrows();
  }

  disconnectedCallback() {
    window.removeEventListener('resize', this.onResize);
  }

  debounce(func, wait) {
    let timeout;
    return (...args) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => func.apply(this, args), wait);
    };
  }

  // Distance from the track's snap edge to each item's leading edge.
  itemOffsets() {
    const trackLeft =
      this.track.getBoundingClientRect().left + (parseFloat(getComputedStyle(this.track).scrollPaddingLeft) || 0);
    return Array.from(this.track.querySelectorAll(this.itemSelector)).map(
      (item) => item.getBoundingClientRect().left - trackLeft,
    );
  }

  scrollToAdjacent(direction) {
    const offsets = this.itemOffsets();
    const target =
      direction > 0 ? offsets.find((offset) => offset > 1) : offsets.reverse().find((offset) => offset < -1);

    if (target === undefined) {
      this.track.scrollBy({ left: direction * this.track.clientWidth * 0.8, behavior: 'smooth' });
      return;
    }
    this.track.scrollBy({ left: target, behavior: 'smooth' });
  }

  setupArrows() {
    if (this.prevButton) {
      this.prevButton.addEventListener('click', () => this.scrollToAdjacent(-1));
    }
    if (this.nextButton) {
      this.nextButton.addEventListener('click', () => this.scrollToAdjacent(1));
    }
  }

  updateArrows() {
    const maxScroll = this.track.scrollWidth - this.track.clientWidth;
    this.classList.toggle('box-content-carousel--static', maxScroll <= 1);
    if (this.prevButton) this.prevButton.disabled = this.track.scrollLeft <= 1;
    if (this.nextButton) this.nextButton.disabled = this.track.scrollLeft >= maxScroll - 1;
    if (this.hasAttribute('data-anchor-next')) this.anchorNextToCurrentItem();
  }

  // Keep the "next" arrow over the current item rather than the peeking one.
  anchorNextToCurrentItem() {
    const items = Array.from(this.track.querySelectorAll(this.itemSelector));
    if (!items.length) return;

    const offsets = this.itemOffsets();
    let current = 0;
    offsets.forEach((offset, index) => {
      if (Math.abs(offset) < Math.abs(offsets[current])) current = index;
    });

    const inset = Math.max(0, this.getBoundingClientRect().right - items[current].getBoundingClientRect().right);
    this.style.setProperty('--carousel-next-inset', `calc(${inset}px + 1rem)`);
  }
}

class BoxContentImagery extends ScrollCarousel {
  get trackSelector() {
    return '.box-content-sub-items';
  }

  get itemSelector() {
    return '.box-content-sub-item';
  }

  connectedCallback() {
    super.connectedCallback();
    if (!this.track) return;

    this.dialog = this.querySelector('.box-content-dialog');
    this.dialogBody = this.dialog ? this.dialog.querySelector('.box-content-dialog__body') : null;

    this.setupDrag();
    this.setupDialog();
  }

  setupDrag() {
    let dragging = false;
    let startX = 0;
    let startScrollLeft = 0;

    const onDown = (event) => {
      // Leave touch / pen gestures to native scrolling.
      if (event.pointerType !== 'mouse' || event.button !== 0) return;
      // Prevent native image/text drag while panning the track.
      event.preventDefault();
      dragging = true;
      startX = event.clientX;
      startScrollLeft = this.track.scrollLeft;
      this.track.classList.add('is-dragging');
    };

    const onMove = (event) => {
      if (!dragging) return;
      this.track.scrollLeft = startScrollLeft - (event.clientX - startX);
    };

    const onUp = () => {
      if (!dragging) return;
      dragging = false;
      this.track.classList.remove('is-dragging');
    };

    this.track.addEventListener('pointerdown', onDown);
    this.track.addEventListener('pointermove', onMove);
    this.track.addEventListener('pointerup', onUp);
    this.track.addEventListener('pointercancel', onUp);
    this.track.addEventListener('pointerleave', onUp);
  }

  setupDialog() {
    if (!this.dialog || !this.dialogBody) return;

    this.track.addEventListener('click', (event) => {
      const card = event.target.closest('.box-content-card');
      if (!card) return;
      // Ignore the click that ends a drag / swipe.
      if (Date.now() - this.lastScrollAt < 150) return;
      this.openDialog(card);
    });

    this.track.addEventListener('keydown', (event) => {
      if (event.key !== 'Enter' && event.key !== ' ') return;
      const card = event.target.closest('.box-content-card');
      if (!card) return;
      event.preventDefault();
      this.openDialog(card);
    });

    const closeButton = this.dialog.querySelector('[data-box-content-dialog-close]');
    if (closeButton) closeButton.addEventListener('click', () => this.dialog.close());

    // Close when the backdrop (area outside the inner panel) is clicked.
    this.dialog.addEventListener('click', (event) => {
      const panel = this.dialog.querySelector('.box-content-dialog__inner');
      if (!panel) return;
      const rect = panel.getBoundingClientRect();
      const outside =
        event.clientX < rect.left ||
        event.clientX > rect.right ||
        event.clientY < rect.top ||
        event.clientY > rect.bottom;
      if (outside) this.dialog.close();
    });
  }

  openDialog(card) {
    const clone = card.cloneNode(true);
    clone.removeAttribute('id');
    clone.removeAttribute('role');
    clone.removeAttribute('tabindex');
    clone.querySelectorAll('[id]').forEach((el) => el.removeAttribute('id'));

    this.dialogBody.replaceChildren(clone);

    if (typeof this.dialog.showModal === 'function') {
      this.dialog.showModal();
    } else {
      this.dialog.setAttribute('open', '');
    }
  }
}

if (!customElements.get('scroll-carousel')) {
  window.customElements.define('scroll-carousel', ScrollCarousel);
}

if (!customElements.get('box-content-imagery')) {
  window.customElements.define('box-content-imagery', BoxContentImagery);
}
